import { ChangeDetectionStrategy, Component, OnInit, ChangeDetectorRef, OnDestroy, inject, input } from '@angular/core';
import { FileDataModel, escrowWaiverType } from 'apps/cmg-clear2-ui/src/app/cmg-1003/models';
import {
  Cmg1003EmploymentIncomeExpensesQuery,
  Cmg1003FileDataQuery,
  Cmg1003LoanQuery,
  Cmg1003TransactionQuery,
} from 'apps/cmg-clear2-ui/src/app/loan-state';
import { Subject, combineLatest, filter, map, switchMap, take, takeUntil } from 'rxjs';
import { splitStringAndNumbers } from '../../../utils';
import { Loan1008Model } from 'apps/cmg-clear2-ui/src/app/loan-1008/models/loan-1008.model';
import { Loan1008Service } from 'apps/cmg-clear2-ui/src/app/loan-1008/loan-1008.service';
import { SessionQuery } from 'apps/cmg-clear2-ui/src/app/core/state';
import { Panel } from 'primeng/panel';
import { PrimeTemplate } from 'primeng/api';
import { AsyncPipe, PercentPipe, CurrencyPipe } from '@angular/common';
import { EmptyToDashPipe } from '../../../pipes/empty-to-dash.pipe';
import { ToNameOrValueNoSelectPipe } from '../../../pipes/to-name-no-select.pipe';
import { InfoPanelContentComponent } from '../components/info-panel-content/info-panel-content.component';
import { InfoRowComponent } from '../components/info-row/info-row.component';

@Component({
  selector: 'cmg-underwriting-information',
  templateUrl: './underwriting-information.component.html',
  styleUrls: ['./underwriting-information.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    Panel,
    PrimeTemplate,
    AsyncPipe,
    PercentPipe,
    CurrencyPipe,
    EmptyToDashPipe,
    ToNameOrValueNoSelectPipe,
    InfoPanelContentComponent,
    InfoRowComponent,
  ],
})
export class UnderwritingInformationComponent implements OnInit, OnDestroy {
  private readonly _cdRef = inject(ChangeDetectorRef);
  private readonly _loanQuery = inject(Cmg1003LoanQuery);
  private readonly _transactionQuery = inject(Cmg1003TransactionQuery);
  private readonly _empIncExpQuery = inject(Cmg1003EmploymentIncomeExpensesQuery);
  private readonly _loan1008Service = inject(Loan1008Service);
  private readonly _fileDataQuery = inject(Cmg1003FileDataQuery);
  private readonly _sessionQuery = inject(SessionQuery);

  readonly fileDataId = input<number>(undefined);

  loan1008Data: Loan1008Model;
  fileData: FileDataModel;
  escrowWaiverType = escrowWaiverType;

  combinedHousingExpenses$ = this._empIncExpQuery.selectActiveEmploymentIncomeExpense().pipe(map((t) => t?.combinedHousingExpenses));
  cashToClose$ = this._transactionQuery.select((t) => t.cashToClose);

  loanInfo$ = this._loanQuery.selectLoanInfo();
  private _destroyed$ = new Subject();

  ngOnInit(): void {
    if (this.fileDataId() > 0) {
      combineLatest({
        fileData: this._sessionQuery
          .select((state) => state.all1003Loaded)
          .pipe(
            filter((loaded) => loaded),
            takeUntil(this._destroyed$),
            switchMap(() => this._fileDataQuery.select()),
          ),
        loan1008Data: this._loan1008Service.getLoan1008data(this.fileDataId()),
      })
        .pipe(take(1))
        .subscribe(({ fileData, loan1008Data }) => {
          this.fileData = fileData;
          this.loan1008Data = loan1008Data;
          this._cdRef.detectChanges();
        });
    }
  }

  ngOnDestroy(): void {
    this._destroyed$.next(true);
    this._destroyed$.complete();
  }

  getMatchString(str) {
    if (this.loan1008Data) {
      return this._isStringExcluded(str) ? '-' : splitStringAndNumbers(str);
    }
  }

  private _isStringExcluded(input: string): boolean {
    const excludeValues = ['select', 'notassigned'];
    const lowerCaseString = input.toLowerCase();
    const stringWithoutSpaces = lowerCaseString.replace(/\s/g, '');
    return excludeValues.includes(stringWithoutSpaces);
  }
}
